import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useCentralizedAuth } from '@/context/CentralizedAuthProvider';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowRight } from 'lucide-react';
import LoadingScreen from '@/components/assessment/LoadingScreen';
import PaymentSuccessHeader from '@/components/payment/PaymentSuccessHeader';
import PaymentSuccessDetails from '@/components/payment/PaymentSuccessDetails';
import CongratulatoryMessage from '@/components/payment/CongratulatoryMessage';
import { handleAssessmentAccess } from '@/utils/assessment/assessmentAccess';

interface OrderDetails {
  packageType: 'silver' | 'gold';
  transactionId: string;
  orderId: string;
  amount: number;
  originalAmount: number;
  discount: number;
  couponCode: string | null;
}

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useCentralizedAuth();
  const [loading, setLoading] = useState(true);
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        // Get order ID from URL parameters
        const params = new URLSearchParams(location.search);
        const orderId = params.get('order');
        
        if (!orderId) {
          toast.error('Order ID not found');
          navigate('/');
          return;
        }

        // Store the order ID in localStorage for assessment
        localStorage.setItem('current_order_id', orderId);
        console.log('Payment success page: Stored order ID in localStorage:', orderId);

        // Fetch order details from Supabase
        const { data, error } = await supabase
          .from('orders')
          .select('*')
          .eq('id', orderId)
          .eq('status', 'completed')
          .single();

        if (error || !data) {
          console.error('Error fetching order:', error);
          toast.error('Could not retrieve order details');
          navigate('/');
          return;
        }

        // Set order details
        setOrderDetails({
          packageType: data.package_type as 'silver' | 'gold',
          transactionId: data.razorpay_payment_id || 'N/A',
          orderId: data.id,
          amount: data.amount,
          originalAmount: data.original_amount,
          discount: data.coupon_discount || 0,
          couponCode: data.coupon_code,
        });
      } catch (error) {
        console.error('Error:', error);
        toast.error('An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [location, navigate]);

  const handleStartAssessment = async () => {
    if (orderDetails) {
      // Pass true to indicate this is from payment success page
      await handleAssessmentAccess(navigate, user?.id, orderDetails.orderId, true);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <LoadingScreen message="Loading order details..." />
        <Footer />
      </div>
    );
  }

  if (!orderDetails) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-grow flex items-center justify-center">
          <Card className="w-full max-w-lg text-center">
            <CardHeader>
              <CardTitle className="text-red-600">Order Not Found</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-4">We couldn't find your order details.</p>
              <Button onClick={() => navigate('/')} variant="outline">
                Return to Home
              </Button>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      
      <main className="flex-grow py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <PaymentSuccessHeader />
          <PaymentSuccessDetails orderDetails={orderDetails} />
          <CongratulatoryMessage />
          
          <div className="text-center">
            <Button 
              onClick={handleStartAssessment}
              className={`text-lg px-8 py-6 h-auto ${orderDetails.packageType === 'silver' 
                ? 'bg-brand-blue hover:bg-brand-blue/90' 
                : 'bg-brand-yellow text-brand-blue hover:bg-amber-400'}`}
              size="lg"
            >
              START ASSESSMENT <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default PaymentSuccess;
